import { faListUl, faRuler } from '@fortawesome/pro-regular-svg-icons'
import { SectionWrapper } from '../SectionWrapper/SectionWrapper'
import { DetailInfo, DetailRow } from '../DetailRow/DetailRow'
import { Truncate } from '@labrav/react-components'
import {
  DurationType,
  EntitlementOption,
  PrivateOffer,
} from '../../../modules/privateOffer/types'
import { useStyles } from './CloudBasedOfferInfo.styles'
import { useTimeZone } from '../../utils/hooks/useTimeZone'
import { ColumnReadMore, TextWithCopyIcon } from '@labrav/common'
import { getCurrencyLabelForDisplay } from '../../../utils/getCurrencyLabelForDisplay'
import { AwsAmiInstance } from '../AwsAmiInstance/AwsAmiInstance'
import { InstallmentsView } from '../InstallmentsView/InstallmentsView'
import { StandardCustomDimensions } from '../StandardCustomDimensions/StandardCustomDimensions'
import { dateAndTimeConversion } from '../../utils/dateAndTimeConversion'
import { calculateUsageEndDateFromDuration } from '../../../utils/amiPrivateOfferUtil'
import {
  ContainerPricingModel,
  Product,
} from '../../../modules/productsListing/types'

export const AWSAMIOfferInfo = ({
  privateOffer,
  currencySymbol,
  product,
}: {
  privateOffer: PrivateOffer
  currencySymbol?: string
  product?: Product
}) => {
  const { classes } = useStyles()
  const { timeZone } = useTimeZone()

  const isContainerPricingModelContract =
    product?.containerPricingModel === ContainerPricingModel.CONTRACT

  const isFlexiblePaymentTerms = privateOffer?.flexiblePaymentTerms
  const entitlementOptionMessage =
    privateOffer?.entitlementOption === EntitlementOption.SINGLE
      ? 'Buyer can choose only one unit of one of the existing dimensions.'
      : 'Buyer can choose one or more existing dimensions'

  return (
    <>
      <SectionWrapper
        heading="Offer details"
        icon={faListUl}
        children={
          <div className={classes.sectionWrapper}>
            <DetailRow
              borderBottom
              children={
                <>
                  <DetailInfo
                    title={'Buyer name'}
                    value={
                      <Truncate width={'70%'} clipboardTooltip showIcon={false}>
                        {privateOffer?.companyName}
                      </Truncate>
                    }
                    keyName={'companyName'}
                    valueClassName={classes.infoValue}
                  />
                  <DetailInfo
                    title={'Buyer AWS account ID'}
                    value={
                      <Truncate width={'70%'} clipboardTooltip>
                        {privateOffer?.awsAccountId}
                      </Truncate>
                    }
                    keyName={'awsAccountId'}
                    valueClassName={classes.infoValue}
                  />
                </>
              }
            />
            <DetailRow
              borderBottom
              children={
                <>
                  <DetailInfo
                    title={'Offer name'}
                    value={
                      <Truncate width={'70%'} clipboardTooltip showIcon={false}>
                        {privateOffer?.privateOfferName}
                      </Truncate>
                    }
                    keyName={'privateOfferName'}
                    valueClassName={classes.infoValue}
                  />
                  <DetailInfo
                    title={'Offer expiration date'}
                    keyName="offerExpirationDate"
                    value={
                      privateOffer?.offerExpirationDate
                        ? (() => {
                            const { modifiedDate, modifiedTime } =
                              dateAndTimeConversion(
                                privateOffer?.offerExpirationDate,
                                timeZone
                              )
                            return (
                              <div className={classes.dateTimeWrapper}>
                                <div className={classes.createdAtDate}>
                                  {modifiedDate}
                                </div>
                                {modifiedTime && (
                                  <div className={classes.createdAtTime}>
                                    {modifiedTime.trim()}
                                  </div>
                                )}
                              </div>
                            )
                          })()
                        : '-'
                    }
                    valueClassName={classes.infoValue}
                  />
                </>
              }
            />
            <DetailRow
              borderBottom={true}
              children={
                <>
                  <DetailInfo
                    title={'Offer description '}
                    gridSize={12}
                    keyName="offerDescription"
                    valueClassName={classes.infoValue}
                    value={
                      privateOffer?.offerDescription ? (
                        <ColumnReadMore
                          customCharForRemoveFromMax={15}
                          initializeWithLowerCase
                          description={privateOffer?.offerDescription || ''}
                        />
                      ) : (
                        '-'
                      )
                    }
                  />
                </>
              }
            />
            <DetailRow
              borderBottom
              children={
                <>
                  <DetailInfo
                    title={'AWS offer ID'}
                    keyName="awsOfferId"
                    value={
                      <TextWithCopyIcon text={privateOffer?.awsOfferId || ''} />
                    }
                    valueClassName={classes.infoValue}
                  />
                  <DetailInfo
                    title={'Renewal'}
                    value={privateOffer?.renewal ? 'Yes' : 'No'}
                    keyName={'renewal'}
                    valueClassName={classes.infoValue}
                  />
                </>
              }
            />
            <DetailRow
              borderBottom
              children={
                <>
                  <DetailInfo
                    title={'Selected currency'}
                    keyName="selectedCurrency"
                    value={getCurrencyLabelForDisplay(
                      privateOffer.currency || 'USD'
                    )}
                    valueClassName={classes.infoValue}
                  />
                  <DetailInfo
                    title={'Pricing model'}
                    value={getPricingModelLabel(privateOffer)}
                    keyName={'pricingModel'}
                    valueClassName={classes.infoValue}
                  />
                </>
              }
            />
            {privateOffer?.pricingModel !== 'usage-pricing' && (
              <DetailRow
                borderBottom
                children={
                  <>
                    <DetailInfo
                      title={'Duration type'}
                      keyName="durationType"
                      value={
                        privateOffer?.durationType
                          ? getDurationTypeLabel(privateOffer?.durationType)
                          : '-'
                      }
                      valueClassName={classes.infoValue}
                    />
                    <DetailInfo
                      title={'Duration'}
                      value={
                        privateOffer?.durationLength
                          ? String(privateOffer?.durationLength)
                          : '-'
                      }
                      keyName={'durationLength'}
                      valueClassName={classes.infoValue}
                    />
                  </>
                }
              />
            )}
            {privateOffer?.pricingModel !== 'installment-plan' && (
              <DetailRow
                borderBottom={false}
                children={
                  <>
                    <DetailInfo
                      title={'Usage end date'}
                      keyName="usageEndDate"
                      value={
                        privateOffer?.usageDuration &&
                        privateOffer?.offerExpirationDate
                          ? (() => {
                              const dateValue =
                                calculateUsageEndDateFromDuration(
                                  privateOffer?.offerExpirationDate,
                                  privateOffer?.usageDuration
                                )
                              const { modifiedDate } = dateAndTimeConversion(
                                dateValue || '',
                                timeZone
                              )
                              return (
                                <div className={classes.dateTimeWrapper}>
                                  <div className={classes.createdAtDate}>
                                    {modifiedDate}
                                  </div>
                                </div>
                              )
                            })()
                          : '-'
                      }
                      valueClassName={classes.infoValue}
                    />
                  </>
                }
              />
            )}
          </div>
        }
      />

      {isContainerPricingModelContract ? (
        <SectionWrapper
          heading="Dimensions details"
          icon={faRuler}
          className={classes.dimensionBox}
          children={
            <div className={classes.dimensionWrapper}>
              {!isFlexiblePaymentTerms && (
                <div className={classes.subTitle}>
                  {entitlementOptionMessage}
                </div>
              )}
              <StandardCustomDimensions
                dimensions={privateOffer?.dimensions}
                flexiblePaymentTerms={privateOffer?.flexiblePaymentTerms}
                currencySymbol={currencySymbol}
              />
            </div>
          }
        />
      ) : (
        <AwsAmiInstance
          dimensions={privateOffer?.dimensions}
          currencySymbol={currencySymbol}
          privateOffer={privateOffer}
        />
      )}
      {privateOffer?.installments && (
        <SectionWrapper
          heading={`Installments (${(privateOffer?.installments || []).length})`}
          icon={faRuler}
          children={
            <InstallmentsView
              installments={privateOffer?.installments || []}
              currencySymbol={currencySymbol}
            />
          }
        />
      )}
    </>
  )
}

const getPricingModelLabel = (privateOffer: PrivateOffer) => {
  if (privateOffer?.pricingModel === 'installment-plan') {
    return 'Installment plan'
  } else if (privateOffer?.pricingModel === 'usage-pricing') {
    return 'Usage pricing'
  } else {
    return 'Upfront payment'
  }
}

const getDurationTypeLabel = (durationType?: DurationType) => {
  if (!durationType) {
    return '-'
  }
  switch (durationType) {
    case DurationType.MONTHS:
      return 'Months'
    case DurationType.YEARS:
      return 'Years'
    case DurationType.WEEKS:
      return 'Weeks'
    case DurationType.DAYS:
      return 'Days'
    default:
      return '-'
  }
}
